import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DEMO_STATUSES, DEMO_STATUS_LABEL, statusBadge, fmtDate } from "@/lib/adminMeta";
import { Search } from "lucide-react";

export default function AdminDemoRequests() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ q: "", status: "all", dms: "", date_from: "", date_to: "", min_inventory: "", max_inventory: "" });

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (f.q) params.q = f.q;
    if (f.status && f.status !== "all") params.status = f.status;
    if (f.dms) params.dms = f.dms;
    if (f.date_from) params.date_from = f.date_from;
    if (f.date_to) params.date_to = f.date_to;
    if (f.min_inventory) params.min_inventory = f.min_inventory;
    if (f.max_inventory) params.max_inventory = f.max_inventory;
    try { const { data } = await api.get("/admin/demo-requests", { params }); setRows(data); }
    catch { setRows([]); } finally { setLoading(false); }
  }, [f]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  return (
    <div className="space-y-6 fade-up" data-testid="admin-demo-requests">
      <h1 className="text-3xl font-bold text-navy">Demo Requests</h1>

      <div className="bg-white rounded-2xl border border-border p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={f.q} onChange={set("q")} placeholder="Search name, dealership, email" className="pl-9" data-testid="demo-search" />
        </div>
        <Select value={f.status} onValueChange={(v) => setF((s) => ({ ...s, status: v }))}>
          <SelectTrigger data-testid="demo-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Statuses</SelectItem>
            {DEMO_STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={f.dms} onChange={set("dms")} placeholder="DMS used" data-testid="demo-filter-dms" />
        <div className="grid grid-cols-2 gap-2">
          <Input type="number" value={f.min_inventory} onChange={set("min_inventory")} placeholder="Min inv" data-testid="demo-filter-min" />
          <Input type="number" value={f.max_inventory} onChange={set("max_inventory")} placeholder="Max inv" data-testid="demo-filter-max" />
        </div>
        <div className="grid grid-cols-2 gap-2 sm:col-span-2">
          <div><label className="text-[11px] text-muted-foreground">From</label><Input type="date" value={f.date_from} onChange={set("date_from")} data-testid="demo-filter-from" /></div>
          <div><label className="text-[11px] text-muted-foreground">To</label><Input type="date" value={f.date_to} onChange={set("date_to")} data-testid="demo-filter-to" /></div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-navy">
              <tr className="text-left">
                {["Date", "Name", "Dealership", "Email", "Phone", "Locations", "Inventory", "DMS", "Status"].map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border" data-testid="demo-table-body">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">No demo requests match your filters.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} onClick={() => navigate(`/admin/demo-requests/${r.id}`)} className="hover:bg-secondary/40 cursor-pointer" data-testid={`demo-row-${r.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-3 font-medium text-navy whitespace-nowrap">{r.name || "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.dealership_name || "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.email || "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.phone || "-"}</td>
                  <td className="px-4 py-3">{r.num_locations || "-"}</td>
                  <td className="px-4 py-3">{r.inventory_size || "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.dms_used || "-"}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${statusBadge(r.status || "new")}`}>{DEMO_STATUS_LABEL[r.status || "new"]}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
